import React, { memo } from 'react';
import BedrockIcon from 'assets/icons/bedrock/bedrock-icon.svg?react';
import BedrockText from 'assets/icons/bedrock/bedrock-text.svg?react';
import OpenaiIcon from 'assets/icons/openai/openai-icon.svg?react';
import OpenaiText from 'assets/icons/openai/openai-text.svg?react';
import AnthropicText from 'assets/icons/anthropic/anthropic-text.svg?react';
import GoogleText from 'assets/icons/google/google-text.svg?react';
import VertexIcon from 'assets/icons/vertex/vertex-icon.svg?react';
import VertexText from 'assets/icons/vertex/vertex-text.svg?react';
import DeepSeekIcon from 'assets/icons/deepseek/deepseek-icon.svg?react';
import ClaudeIcon from 'assets/icons/claude/claude-icon.svg?react';
import GeminiIcon from 'assets/icons/gemini/gemini-icon.svg?react';
import PalmIcon from 'assets/icons/palm/palm-icon.svg?react';
import MistralIcon from 'assets/icons/mistral/mistral-icon.svg?react';
import CohereIcon from 'assets/icons/cohere/cohere-icon.svg?react';
import PerplexityIcon from 'assets/icons/perplexity/perplexity-icon.svg?react';
import PerplexityText from 'assets/icons/perplexity/perplexity-text.svg?react';
import XaiIcon from 'assets/icons/xai/xai-icon.svg?react';
import XaiText from 'assets/icons/xai/xai-text.svg?react';
import OllamaIcon from 'assets/icons/ollama/ollama-icon.svg?react';
import OllamaText from 'assets/icons/ollama/ollama-text.svg?react';
import { ModelAvatar, CombinedIcon } from './LLMIcon.styles';
import { ModelType } from 'api/model';

interface LLMIconProps {
  provider?: ModelType;
  model?: string;
  size?: number;
}

const LLMIcon: React.FC<LLMIconProps> = ({ provider, model, size = 24 }) => {
  const avatarIconProps = {
    height: size,
    width: size,
    style: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) scale(0.85)',
    },
  };

  const getIconComponent = () => {
    if (provider && !model) {
      switch (provider) {
        case ModelType.OPEN_AI:
          return (
            <CombinedIcon>
              <OpenaiIcon height={size} />
              <OpenaiText height={size} />
            </CombinedIcon>
          );
        case ModelType.ANTHROPIC:
          return <AnthropicText height={size} />;
        case ModelType.GOOGLE:
          return <GoogleText height={size} />;
        case ModelType.VERTEX:
          return (
            <CombinedIcon>
              <VertexIcon height={size} />
              <VertexText height={size} />
            </CombinedIcon>
          );
        case ModelType.BEDROCK:
          return (
            <CombinedIcon>
              <BedrockIcon height={size} />
              <BedrockText height={size} />
            </CombinedIcon>
          );
        case ModelType.PERPLEXITY:
          return (
            <CombinedIcon>
              <PerplexityIcon height={size} />
              <PerplexityText height={size} />
            </CombinedIcon>
          );
        case ModelType.XAI:
          return (
            <CombinedIcon>
              <XaiIcon height={size} />
              <XaiText height={size} />
            </CombinedIcon>
          );
        case ModelType.OLLAMA:
          return (
            <CombinedIcon>
              <OllamaIcon height={size} />
              <OllamaText height={size} />
            </CombinedIcon>
          );
        default:
          return (
            <CombinedIcon>
              <OpenaiIcon height={size} />
              <OpenaiText height={size} />
            </CombinedIcon>
          );
      }
    }

    if (model) {
      const modelLower = model.toLowerCase();

      if (modelLower.includes('gpt-3')) {
        return (
          <ModelAvatar size={size} color="rgb(25, 195, 125)">
            <OpenaiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('gpt-4')) {
        return (
          <ModelAvatar size={size} color="rgb(171, 104, 255)">
            <OpenaiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('o1')) {
        return (
          <ModelAvatar size={size} color="rgb(249, 195, 34)">
            <OpenaiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('o3')) {
        return (
          <ModelAvatar size={size} color="rgb(52, 152, 219)">
            <OpenaiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('deepseek')) {
        return (
          <ModelAvatar size={size} color="rgb(77, 107, 254)">
            <DeepSeekIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('claude')) {
        return (
          <ModelAvatar size={size} color="rgb(217, 119, 87)">
            <ClaudeIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('gemini')) {
        return (
          <ModelAvatar className="flex justify-center items-center" size={size} color="rgb(66, 133, 244)">
            <GeminiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('bison')) {
        return (
          <ModelAvatar size={size} color="#fff">
            <PalmIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('llama')) {
        return (
          <ModelAvatar size={size} color="#fff">
            <OllamaIcon {...avatarIconProps} color="#000" />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('mistral') || modelLower.includes('mixtral')) {
        return (
          <ModelAvatar size={size} color="rgb(255, 69, 0)">
            <MistralIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('cohere') || modelLower.includes('command')) {
        return (
          <ModelAvatar size={size} color="rgb(38, 115, 57)">
            <CohereIcon {...avatarIconProps} />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('sonar')) {
        return (
          <ModelAvatar size={size} color="#22B8CD">
            <PerplexityIcon {...avatarIconProps} className="scale-90" />
          </ModelAvatar>
        );
      }

      if (modelLower.includes('grok')) {
        return (
          <ModelAvatar size={size} color="#05060D">
            <XaiIcon {...avatarIconProps} className="scale-90" />
          </ModelAvatar>
        );
      }
    }

    // Fallback to provider icon if model is specified but doesn't match any known patterns
    switch (provider) {
      case ModelType.OLLAMA:
        return (
          <ModelAvatar size={size} color="#fff">
            <OllamaIcon {...avatarIconProps} color="#000" />
          </ModelAvatar>
        );
      default:
        return (
          <ModelAvatar size={size} color="rgb(16, 163, 127)">
            <OpenaiIcon {...avatarIconProps} />
          </ModelAvatar>
        );
    }
  };

  const IconComponent = getIconComponent();

  return IconComponent;
};

export default memo(LLMIcon);
